<template>
	<chatbot
		:alerts="alerts"
		:translations="translations"
		:code="code"
		:market="market"
		:cardUrl="customerData.cardUrl"
		:prevent-unload="preventUnload"
		:show-chatbot="showChatbot"
		@close="closeChat"
	/>
</template>

<script>
import Message from "./chatbotInstructions/Message";
import Call from "./chatbotInstructions/Call";
import Select from "./chatbotInstructions/Select";
import SelectOption from "./chatbotInstructions/SelectOption";
import Form from "./chatbotInstructions/Form";
import FormField from "./chatbotInstructions/FormField";
import Custom from "./chatbotInstructions/Custom";
import FormFieldOption from "./chatbotInstructions/FormFieldOption";

export default {
	props: {
		customerData: {
			type: Object,
			required: true,
		},
		countryCode: {
			type: Number,
			required: true,
			default: 1
		},
		market: {
			type: String,
			required: true,
			default: 'monizze'
		},
		employeesData: {
			type: Array,
			required: true,
		},
		translations: {
			type: Object,
			required: true,
		},
		urls: {
			type: Object,
			required: true,
		},
		alerts: {
			type: Object,
			required: true,
		},
		showChatbot: {
			type: Boolean,
			required: true,
		}
	},
	data ()
	{
		const self = this;

		const trans = this.translations.employee_update.message;

		return {
			preventUnload: false,
			employeeId: 0,
			employeeUpdate: [],
			code: {
				/*
				 * Entry point
				 */
				main: [
					new Call('manual_or_upload'),
				],
				manual_or_upload: [
					new Message(
						function()
						{
							return trans.main.intro.replace('|number|', self.employeesData.length);
						}
					),
					new Message(trans.main.options),
					new Select(
						'nextCall',
						[
							new SelectOption('manual', trans.options.manual),
							new SelectOption('file', trans.options.upload),
						]
					),
					new Message((variables) => trans.manual_or_upload.options[variables.nextCall])
						.isFromUser(),
					new Call((variables) => variables.nextCall),
				],
				manual: [
					new Message(
						function(variables)
						{
							variables.country_id = self.countryCode;
							variables.street = '';
							variables.number = '';
							variables.box = '';
							variables.zip = '';
							variables.city = '';
							let employee = self.getNextEmployee();
							variables.id = employee.id;
							return trans.manual.intro.replace('|firstname|', employee.firstname + ' ' + employee.lastname);
						}
					),
					new Form(
						trans.manual.button,
						[
							new FormField('country_id', trans.manual.fields.country + ' *')
								.isRequired()
								.isSelect(
									self.customerData.countries
										.map((country) => new FormFieldOption(country.value, country.label))
								),
							new FormField('street', trans.manual.fields.street + ' *')
								.setWidth('is-three-fifths')
								.isRequired(),
							new FormField('number', trans.manual.fields.number + ' *')
								.setWidth('is-one-fifth')
								.isRequired(),
							new FormField('box', trans.manual.fields.box)
								.setWidth('is-one-fifth'),
							new FormField('zip', trans.manual.fields.zip + ' *')
								.setWidth('is-one-quarter')
								.isRequired(),
							new FormField('city', trans.manual.fields.city + ' *')
								.setWidth('')
								.isRequired(),
						]
					),
					new Message((variables) => self.formatSelectedAddress(variables))
						.isFromUser(false),
					new Custom(
						function(variables)
						{
							self.saveAddress(variables);
						}
					),
					new Call(() => self.isFinish() ? 'finish' : 'manual'),
				],
				file: [
					new Message(trans.file.intro),
					new Select(
						'nextCall',
						[
							new SelectOption('download', trans.file.options.template).hasHref(self.urls.template),
							new SelectOption('upload', trans.file.options.upload),
						]
					),
					new Message((variables) => trans.file.options[variables.nextCall])
						.isFromUser(),

					new Call((variables) => variables.nextCall),
				],
				download: [
					new Message(trans.download.intro),
					new Select(
						null,
						[new SelectOption(null, trans.download.button)]
					),
					new Message(trans.download.response).isFromUser(),
					new Call('upload'),
				],
				upload: [
					new Message(trans.upload.intro),
					new Call(
						function(variables)
						{
							self.preventUnload = true;
							clearError();
						}
					),
					new Form(
						trans.upload.submit,
						[
							new FormField('file', trans.upload.field).isFile().isRequired(),
						]
					).validateWith(
						function(variables)
						{
							let formData = new FormData();
							formData.append('file', variables.file[0]);
							variables.fileId = 0;

							return new ApiMonizze()
								.postDataFromApi(
									self.urls.send,
									formData,
									function(data)
									{
										variables.fileId = data.fileId;
									}
								);
						}
					),
					new Message(trans.check.intro),
					new Message(trans.check.time),
					new Call('check')
				],
				check : [
					new Custom(
						async function(variables)
						{
							let url = self.urls.check.replace('|fileId|', variables.fileId);

							let donePooling = false;
							while(!donePooling)
							{
								await new ApiMonizze()
									.postDataFromApi(
										url,
										{},
										function (data) {
											donePooling = data.fileStatus === 'done';
										}
									);
								if( !donePooling )
								{
									// We wait 1sec between retry
									await new Promise(resolve => setTimeout(resolve, 1000));
								}
							}
						}
					),
					new Call('uploaded')
				],
				uploaded:[
					new Call(
						function(variables)
						{
							self.preventUnload = false;
						}
					),
					new Message(trans.upload.response),
					new Call('finalize')
				],
				finish: [
					new Message(trans.finish.response),
					new Select(
						'nextCall',
						[
							new SelectOption('save', trans.finish.save),
							new SelectOption('quit', trans.finish.quit),
						]
					),
					new Message((variables) => trans.finish[variables.nextCall])
						.isFromUser(),
					new Call((variables) => variables.nextCall),
				],
				finalize: [
					new Select(
						'nextCall',
						[
							new SelectOption('quit', trans.finalize.quit),
						]
					),
					new Call('quit')
				],
				save : [
					new Custom(
						function(variables)
						{
							return (new ApiMonizze())
								.postDataFromApi(
									self.urls.bulk,
									{
										employees : self.employeeUpdate,
									},
									function(data, response)
									{
										variables.saved = true;
									}
								);
						}
					),
					new Message(trans.finish.options.save),
				],
				quit : [
					new Message(trans.finish.options.quit)
						.isFromUser(),
					new Custom(
						function()
						{
							self.$emit('close');
						}
					),
				]
			},
		}
	},
	methods: {
		saveAddress(variables)
		{
			this.employeeUpdate.push({
				street: variables.street,
				number: variables.number,
				box: variables.box,
				zip: variables.zip,
				city: variables.city,
				country_id: variables.country_id,
				id: variables.id,
			});
		},
		formatSelectedAddress(variables)
		{
			let country = this.getCountryByCode(variables.country_id);
			let message = variables.street + ' ' + variables.number;

			if ( variables.box )
			{
				message += ' / ' + variables.box;
			}

			message += '<br/>' + variables.zip + ' ' + variables.city
				+ '<br/>' + country;

			return message;
		},
		getCountryByCode(code)
		{
			for (const country of this.customerData.countries)
			{
				if ( country.value == code )
				{
					return country.label;
				}
			}
		},
		getNextEmployee()
		{
			let employee = this.employeesData[this.employeeId];
			this.employeeId++;
			return employee;
		},
		isFinish()
		{
			return this.employeeId === this.employeesData.length;
		},
		closeChat()
		{
			this.$emit('close');
		}
	}

}
</script>
